import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Login from './components/Login';
import Register from './components/Register';
import AguardoLiberacao from './components/AguardoLiberacao';
import Overview from './components/Overview';
import ProjectManagement from './components/ProjectManagement';
import TestCases from './components/TestCases';
import Executions from './components/Executions';
import Users from './components/Users';
import MyProfile from './components/MyProfile';
import UserManagement from './components/UserManagement';
import Status from './components/Status';
import GitHubCallback from './components/GitHubCallback';
import GitHubError from './components/GitHubError';
import GoogleCallback from './components/GoogleCallback';
import MicrosoftCallback from './components/MicrosoftCallback';
import Dashboard from './components/Dashboard';
import './App.css';
import AppLayout from './layouts/AppLayout';
import Changelog from './components/Changelog';
import ChangelogAdmin from './components/ChangelogAdmin';
import ChangelogPublic from './components/ChangelogPublic';
  import Issues from './components/Issues';
  import TestRepository from './components/TestRepository';
  import IntegrationsV2 from './components/IntegrationsV2';
import { TutorialProvider } from './contexts/TutorialContext';
import TourOverlay from './components/TourOverlay';

// Componente para proteger rotas que precisam de autenticação
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Componente para redirecionar usuários logados
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }
  
  return user ? <Navigate to="/overview" /> : children;
};

const RequirePermission = ({ permissionKey, fallbackRoles = [], children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }
  const raw = (user?.role || '').toUpperCase();
  const normalized = raw === 'ADMIN' || raw === 'ADM' ? 'ADM'
    : raw === 'MANAGER' || raw === 'GERENCIAL' ? 'GERENCIAL'
    : raw === 'TESTER' || raw === 'QA' ? 'QA'
    : raw === 'DEVELOPER' || raw === 'DESIGNER' || raw === 'DEV' ? 'DEV'
    : raw === 'VIEWER' || raw === 'CONSULTA' || raw === 'READONLY' ? 'DEV'
    : raw;
  return normalized === 'ADM' ? children : <Navigate to="/overview" />;
};

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <Router>
          <TutorialProvider>
            <div className="App">
            <TourOverlay />
            <Routes>
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            <Route 
              path="/aguardo-liberacao" 
              element={
                <PublicRoute>
                  <AguardoLiberacao />
                </PublicRoute>
              } 
            />

            {/* Rotas protegidas aninhadas sob AppLayout para manter sidebar fixa */}
            <Route 
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/overview" element={<Overview />} />
              <Route path="/changelog" element={<Changelog />} />
              {/* Publicar changelog é uso interno — só ADM. Sem essa proteção
                  na rota, qualquer papel que digitasse a URL abria a tela de
                  gerenciar direto, sem depender do botão estar escondido. */}
              <Route path="/changelog/admin" element={<RequirePermission permissionKey="changelog.manage" fallbackRoles={["ADM"]}><ChangelogAdmin /></RequirePermission>} />
              <Route path="/projects" element={<ProjectManagement />} />
              <Route path="/test-cases" element={<TestCases />} />
              <Route path="/test-repository" element={<TestRepository />} />
              <Route path="/issues" element={<Issues />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/executions" element={<Executions />} />
              <Route path="/users" element={<Users />} />
              <Route path="/meu-perfil" element={<MyProfile />} />
              <Route path="/user-management" element={<UserManagement />} />
              {/* Milestones virou a aba "Planos de Teste" dentro de Repositório de Testes — mantém o link antigo redirecionando em vez de quebrar */}
              <Route path="/milestones" element={<Navigate to="/test-repository" />} />
              {/* Configurações removida (tela decorativa, nunca chegou a persistir nada de verdade) — link antigo/favorito redireciona em vez de dar tela em branco */}
              <Route path="/settings" element={<Navigate to="/overview" />} />
              <Route path="/status" element={<RequirePermission permissionKey="status.read" fallbackRoles={["ADM"]}><Status /></RequirePermission>} />
              <Route path="/integrations-v2" element={<IntegrationsV2 />} />
              {/* Páginas placeholder em desenvolvimento - removidas para v.0.0 */}
            </Route>
            <Route 
              path="/auth/github/success" 
              element={<GitHubCallback />} 
            />
            <Route 
              path="/auth/github/error" 
              element={<GitHubError />} 
            />
            <Route
              path="/auth/google/success"
              element={<GoogleCallback />}
            />
            <Route
              path="/auth/google/error"
              element={<GoogleCallback />}
            />
            <Route
              path="/auth/microsoft/success"
              element={<MicrosoftCallback />}
            />
            <Route
              path="/auth/microsoft/error"
              element={<MicrosoftCallback />}
            />
            <Route path="/" element={<Navigate to="/overview" />} />
            <Route 
              path="/changelog-public" 
              element={
                <PublicRoute>
                  <ChangelogPublic />
                </PublicRoute>
              } 
            />
            </Routes>
            </div>
          </TutorialProvider>
        </Router>
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;
